import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { LuArrowLeft, LuPackageCheck, LuPenSquare } from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import DetailAccountInfo from "../../components/materialLines/DetailAccountInfo";
import DetailHeader from "../../components/materialLines/DetailHeader";
import DetailPartyInfo from "../../components/materialLines/DetailPartyInfo";
import DetailSection from "../../components/materialLines/DetailSection";
import MaterialPhoto from "../../components/materialLines/MaterialPhoto";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import {
  detailCardClass,
  detailInfoLabelClass,
  detailTableHeadRowClass,
  detailTableWrapClass,
  mobileCardClass,
  moneyCellClass,
  pageTitleClass,
  tableHeadCellClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import {
  clearCurrentTurningReceive,
  fetchTurningReceiveDetail,
  getCurrentTurningReceive,
  getCurrentTurningReceiveLoading,
} from "../../features/turningReceives/turningReceivesSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import { makeReport } from "../../services/download.service";
import { formatLength, lineName } from "../../static/cuttingLine";
import {
  formatAmount,
  formatGujaratiLongDateTime,
  formatQty,
} from "../../static/materialLine";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { BreadcrumbType } from "../../types/common";
import { TurningReceiveItemProps } from "../../types/turningReceive";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "ટર્નિંગ રિસીવ", href: route.turningReceives },
  { label: "ટર્નિંગ રિસીવ વિગતો" },
];

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const orderLinkClass = "font-medium text-[#113F67] hover:underline";

const closedBadgeClass = "bg-green-100 text-green-800 hover:bg-green-100";

const totalCellClass = "font-bold text-[#113F67]";

const orderPath = (id: number) =>
  route.turningOrderDetail.replace(":id", id as unknown as string);

const TurningReceiveDetailPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const receive = useAppSelector(getCurrentTurningReceive);
  const isLoading = useAppSelector(getCurrentTurningReceiveLoading);
  const loggingUser = useAppSelector(getLoggingUser);
  const sizeUnit = useAppSelector(getSizeUnit);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.turningReceivesUpdate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.turningReceivesReport
  );
  // Rates and amounts only for a user who may see them.
  const canRate = hasPermission(
    loggingUser?.permissions,
    permission.turningRate
  );
  const canReadOrders = hasPermission(
    loggingUser?.permissions,
    permission.turningOrdersRead
  );

  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);

  useEffect(() => {
    if (id) {
      dispatch(fetchTurningReceiveDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentTurningReceive());
    };
  }, [id, dispatch]);

  const items = receive?.items || [];
  const totalQty = items.reduce((sum, item) => sum + (Number(item.qty) || 0), 0);

  // The orders the receive has lines of.
  const orderIds = Array.from(
    new Set(
      items
        .map((item) => item.order_item?.order_id || 0)
        .filter((orderId) => orderId > 0)
    )
  ).sort((a, b) => a - b);

  // Back to where the user came from, or to the receive list when the page was opened directly.
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(route.turningReceives);
    }
  };

  const handleReportExport = async () => {
    setIsReportLoading(true);
    const file = await makeReport(`/v1/admin/turning-receives/${id}/pdf`);
    setIsReportLoading(false);

    if (file) dispatch(updatePdfFile(file));
  };

  const orderLink = (orderId: number) =>
    canReadOrders ? (
      <Link key={orderId} to={orderPath(orderId)} className={orderLinkClass}>
        #{orderId}
      </Link>
    ) : (
      <span key={orderId} className="font-medium text-[#113F67]">
        #{orderId}
      </span>
    );

  const photo = (item: TurningReceiveItemProps) => (
    <MaterialPhoto
      name={item.item?.name || "-"}
      imageUrl={item.item?.image_url}
      className="h-10 w-10 shrink-0 rounded-md"
      compact
    />
  );

  const itemText = (item: TurningReceiveItemProps) =>
    lineName(item.item?.name, item.size?.name, sizeUnit);

  // The piece's material and cut length, as the order line copied them; turning used no material of its own.
  const pieceText = (item: TurningReceiveItemProps) =>
    item.order_item
      ? `${item.order_item.material?.name || "-"} • ${
          item.order_item.cut_length !== null
            ? formatLength(item.order_item.cut_length, sizeUnit)
            : "-"
        }`
      : "-";

  // This receive closed the order line even though fewer pieces came.
  const closedBadge = (item: TurningReceiveItemProps) =>
    item.close_order && (
      <Badge variant="none" className={closedBadgeClass}>
        ઓર્ડર બંધ
      </Badge>
    );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>ટર્નિંગ રિસીવ વિગતો</h1>
      </div>

      {isLoading ? (
        <div className={detailCardClass}>
          <LoadingState />
        </div>
      ) : !receive ? (
        <div className={cx(detailCardClass, "text-center text-gray-500")}>
          ટર્નિંગ રિસીવ ની વિગતો મળી નથી
        </div>
      ) : (
        <div className={detailCardClass}>
          <DetailHeader icon={LuPackageCheck} title="ટર્નિંગ રિસીવ" id={receive.id}>
            <Button variant="outline" className="max-sm:flex-1" onClick={handleBack}>
              <LuArrowLeft className="h-4 w-4" />
              પાછા જાઓ
            </Button>

            {canReport && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                disabled={isReportLoading}
                onClick={handleReportExport}
              >
                {isReportLoading ? "રિપોર્ટ બની રહ્યો છે..." : "રિપોર્ટ બનાવો"}
              </Button>
            )}

            {canUpdate && (
              <Button
                variant="primary"
                className="max-sm:flex-1"
                onClick={() =>
                  navigate(
                    route.turningReceiveEdit.replace(
                      ":id",
                      receive.id as unknown as string
                    )
                  )
                }
              >
                <LuPenSquare className="h-4 w-4" />
                એડિટ કરો
              </Button>
            )}
          </DetailHeader>

          <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>તારીખ:</span>
              <span>{formatGujaratiLongDateTime(receive.date)}</span>
            </div>

            <div className="flex flex-wrap items-center gap-x-2">
              <span className={detailInfoLabelClass}>ઓર્ડર:</span>
              <span className="flex flex-wrap items-center gap-x-3">
                {orderIds.length > 0 ? orderIds.map(orderLink) : "-"}
              </span>
            </div>

            {canRate && (
              <div className="flex flex-wrap items-center gap-x-2">
                <span className={detailInfoLabelClass}>કુલ રકમ:</span>
                <span className="font-semibold text-[#113F67]">
                  ₹{formatAmount(receive.total)}
                </span>
              </div>
            )}
          </div>

          <DetailPartyInfo partyId={receive.party_id} party={receive.party} />

          <DetailSection title="ટર્નિંગ રિસીવ આઇટમ્સ ની માહિતી">
            <div className={cx(detailTableWrapClass, "max-sm:hidden")}>
              <Table>
                <TableHeader>
                  <TableRow className={detailTableHeadRowClass}>
                    <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                    <TableHead className={tableHeadCellClass}>ફોટો</TableHead>
                    <TableHead className={tableHeadCellClass}>આઈટમ</TableHead>
                    <TableHead className={tableHeadCellClass}>ઓર્ડર</TableHead>
                    <TableHead className={tableHeadCellClass}>મટીરીયલ</TableHead>
                    {canRate && (
                      <TableHead className={cx(tableHeadCellClass, "text-right")}>
                        ભાવ
                      </TableHead>
                    )}
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      જથ્થો
                    </TableHead>
                    {canRate && (
                      <TableHead className={cx(tableHeadCellClass, "text-right")}>
                        રકમ
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{index + 1}</TableCell>
                      <TableCell>{photo(item)}</TableCell>
                      <TableCell>
                        <span className="inline-flex flex-wrap items-center gap-2">
                          <span className="break-words font-semibold text-[#113F67]">
                            {itemText(item)}
                          </span>
                          {closedBadge(item)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.order_item ? orderLink(item.order_item.order_id) : "-"}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {pieceText(item)}
                      </TableCell>
                      {canRate && (
                        <TableCell className="text-right">
                          {formatAmount(item.rate)}
                        </TableCell>
                      )}
                      <TableCell className="text-right text-lg font-bold">
                        {formatQty(item.qty)}
                      </TableCell>
                      {canRate && (
                        <TableCell className={moneyCellClass}>
                          {formatAmount(item.amount)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>

                <TableFooter>
                  <TableRow className="bg-[#113F67]/10 hover:bg-[#113F67]/10">
                    <TableCell
                      colSpan={canRate ? 6 : 5}
                      className={cx(totalCellClass, "text-right")}
                    >
                      કુલ:
                    </TableCell>
                    <TableCell className={cx(totalCellClass, "text-right text-lg")}>
                      {formatQty(totalQty)}
                    </TableCell>
                    {canRate && (
                      <TableCell className={cx(totalCellClass, "text-right text-lg")}>
                        {formatAmount(receive.total)}
                      </TableCell>
                    )}
                  </TableRow>
                </TableFooter>
              </Table>
            </div>

            <div className="space-y-4 sm:hidden">
              {items.map((item, index) => (
                <div key={item.id} className={mobileCardClass}>
                  <div className="flex items-center gap-3 border-b p-4">
                    {photo(item)}
                    <span className="min-w-0">
                      <span className="block break-words font-semibold text-[#113F67]">
                        {itemText(item)}
                      </span>
                      {closedBadge(item)}
                    </span>
                  </div>

                  <div className="space-y-3 p-4 text-sm">
                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>ક્રમ</span>
                      <span className="font-medium">{index + 1}</span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>ઓર્ડર</span>
                      <span>
                        {item.order_item ? orderLink(item.order_item.order_id) : "-"}
                      </span>
                    </div>

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>મટીરીયલ</span>
                      <span className="text-right text-gray-600">
                        {pieceText(item)}
                      </span>
                    </div>

                    {canRate && (
                      <div className={cardRowClass}>
                        <span className={cardLabelClass}>ભાવ</span>
                        <span>{formatAmount(item.rate)}</span>
                      </div>
                    )}

                    <div className={cardRowClass}>
                      <span className={cardLabelClass}>જથ્થો</span>
                      <span className={moneyCellClass}>{formatQty(item.qty)}</span>
                    </div>

                    {canRate && (
                      <div className={cardRowClass}>
                        <span className={cardLabelClass}>રકમ</span>
                        <span className={moneyCellClass}>
                          {formatAmount(item.amount)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              <div className={cx(mobileCardClass, "space-y-3 p-4 text-sm")}>
                <div className={cardRowClass}>
                  <span className={totalCellClass}>કુલ નંગ</span>
                  <span className={cx(totalCellClass, "text-right")}>
                    {formatQty(totalQty)}
                  </span>
                </div>
                {canRate && (
                  <div className={cardRowClass}>
                    <span className={totalCellClass}>કુલ રકમ</span>
                    <span className={cx(totalCellClass, "text-right")}>
                      {formatAmount(receive.total)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </DetailSection>

          <DetailAccountInfo
            creator={receive.creator}
            updater={receive.updater}
            createdAt={receive.created_at}
            updatedAt={receive.updated_at}
          />
        </div>
      )}
    </AppLayout>
  );
};
export default TurningReceiveDetailPage;
